import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { logbookGaps, type LogbookGap, type LogbookView, type Trip } from '@kontor/shared';
import { api, queryString } from '../../api';
import { CollapsibleCard } from '../../components/CollapsibleCard';
import { useSession } from '../../session';
import { TripEdit, TripTable } from './TripTable';
import { errorText, formatDay, kmText } from './shared';
import './fleet.css';

/**
 * Fahrtenbuch im Fahrzeug (KON-252): alle Fahrten des Fahrzeugs, Lücken und Überschneidungen als eigene Zeilen, Summen und der Export als Excel, PDF und CSV. Einträge aus Fahrten in Wirkia Drivers
 * entstehen automatisch (Kilometerstand vom Fahrer bestätigt, Strecke aus den Positionsdaten), alles andere trägt das Büro nach. Das Eintragen einer Fahrt steht oben im Fahrzeug.
 */
/** `actions` und `children` kommen von der Fahrzeugmaske: der Knopf „Fahrt eintragen“ im Kopf und das Formular über der Liste */
export function LogbookPanel({ vehicleId, onChanged, open, onOpenChange, actions, children }: { vehicleId: string; onChanged: () => void; open?: boolean; onOpenChange?: (open: boolean) => void; actions?: ReactNode; children?: ReactNode }) {
  const { can } = useSession();
  const client = useQueryClient();
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState<string>(String(thisYear));
  const [editing, setEditing] = useState<Trip | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const range = year === 'all' ? { from: '', to: '' } : { from: `${year}-01-01`, to: `${year}-12-31` };
  const view = useQuery({
    queryKey: ['fleet-logbook', vehicleId, range.from, range.to],
    queryFn: async () => (await api<{ data: LogbookView }>('GET', `/fleet/vehicles/${vehicleId}/logbook-view${queryString(range)}`)).data,
  });
  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['fleet-logbook', vehicleId] });
    onChanged();
  };
  const remove = useMutation({
    mutationFn: (t: Trip) => api('DELETE', `/fleet/trips/${t.id}`),
    onSuccess: () => {
      refresh();
      setMessage({ ok: true, text: 'Die Fahrt ist gelöscht. Das Löschen steht im Protokoll.' });
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Löschen hat nicht geklappt.') }),
  });

  const d = view.data;
  // Neueste zuerst; die Lücke zwischen zwei Fahrten steht über der älteren von beiden
  const trips = d ? [...d.trips].reverse() : [];
  const gapsBefore = new Map<string, LogbookGap>((d?.gaps ?? logbookGaps(d?.trips ?? [])).map((g) => [g.afterTripId, g]));
  const exportLink = (format: 'xlsx' | 'pdf' | 'csv') => `/api/v1/fleet/vehicles/${vehicleId}/logbook${queryString({ ...range, format })}`;

  return (
    <CollapsibleCard title="Fahrtenbuch" storageKey="fleet-trips" defaultOpen open={open} onOpenChange={onOpenChange} actions={actions}>
      {children}
      <div className="fleet-logbook-bar">
        <label className="field fleet-logbook-year">
          <span>Zeitraum</span>
          <select className="input" value={year} onChange={(e) => setYear(e.target.value)}>
            {Array.from({ length: 6 }, (_, i) => String(thisYear - i)).map((y) => <option key={y} value={y}>{y}</option>)}
            <option value="all">Alle Jahre</option>
          </select>
        </label>
        <div className="fleet-logbook-export" role="group" aria-label="Fahrtenbuch exportieren">
          <a className="btn btn--secondary btn--sm" href={exportLink('xlsx')}>Excel</a>
          <a className="btn btn--secondary btn--sm" href={exportLink('pdf')}>PDF</a>
          <a className="btn btn--secondary btn--sm" href={exportLink('csv')}>CSV</a>
        </div>
      </div>
      {d && (
        <p className="muted small">
          {d.trips.length} Fahrten · {kmText(d.totals.km)} gesamt, davon {kmText(d.totals.businessKm)} geschäftlich, {kmText(d.totals.privateKm)} privat, {kmText(d.totals.commuteKm)} Wohnung und Arbeitsstätte.
          {d.totals.gapKm > 0 && <> <strong className="fleet-warn">{kmText(d.totals.gapKm)} nicht erfasst (Lücken).</strong></>}
        </p>
      )}
      {d && d.gaps.length === 0 && d.trips.length > 1 && <p className="small">Das Fahrtenbuch ist in diesem Zeitraum lückenlos.</p>}
      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}
      {view.isError && <div className="alert alert--error" role="alert">{errorText(view.error, 'Das Fahrtenbuch konnte nicht geladen werden.')}</div>}
      {editing && (
        <TripEdit
          key={editing.id}
          trip={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
            setMessage({ ok: true, text: 'Die Fahrt ist geändert und als „geändert“ vermerkt.' });
          }}
        />
      )}
      <TripTable
        trips={trips}
        loading={view.isLoading}
        gapsBefore={gapsBefore}
        onEdit={can('fleet.write') ? (t) => setEditing(t) : undefined}
        onDelete={can('fleet.delete') ? (t) => window.confirm(`Fahrt vom ${formatDay(t.tripDate)} (${t.fromPlace} → ${t.toPlace}) löschen? Das Löschen steht im Protokoll.`) && remove.mutate(t) : undefined}
      />
    </CollapsibleCard>
  );
}
